import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { forwardRef, type ReactNode } from 'react';
import { Link as EnlaceRuta } from 'react-router-dom';
import { errorDeApi, tipoDeFallo } from '../api/http';
import { CANAL_FOLIO } from './tokens';

/**
 * Rotulo de seccion.
 *
 * Un formulario de historia clínica tiene diez bloques. Se ordenan con un
 * rotulo en versalitas y una linea, no con tarjetas anidadas: en una pantalla
 * de consultorio hay que poder recorrer los bloques de un barrido de ojos.
 */
export function Rotulo({ children, accion }: { children: ReactNode; accion?: ReactNode }) {
  return (
    <Box sx={{ mb: 'var(--space-4)', mt: 'var(--space-2)' }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ gap: 'var(--space-5)' }}
      >
        <Typography variant="overline" component="h2" sx={{ color: 'var(--text-secondary)' }}>
          {children}
        </Typography>
        {accion}
      </Stack>
      <Divider sx={{ mt: 'var(--space-2)' }} />
    </Box>
  );
}

export function Seccion({
  titulo,
  descripcion,
  accion,
  children,
  id,
}: {
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
  children: ReactNode;
  /** Ancla para el indice de secciones de un formulario largo. */
  id?: string;
}) {
  return (
    <Box
      component="section"
      id={id}
      sx={{ mb: 'var(--space-8)', scrollMarginTop: 'var(--space-8)' }}
    >
      <Rotulo accion={accion}>{titulo}</Rotulo>
      {descripcion && (
        <Typography
          variant="body2"
          sx={{ color: 'var(--text-secondary)', mb: 'var(--space-4)', maxWidth: '68ch' }}
        >
          {descripcion}
        </Typography>
      )}
      {children}
    </Box>
  );
}

/**
 * Superficie con borde: la unidad de agrupacion del producto.
 *
 * Papel sobre el lienzo: filete de 1px, radio de 8px, sin sombra. La cabecera
 * no lleva banda de color; el cuerpo a sangre (tablas) se separa con un filete.
 */
export function Panel({
  titulo,
  descripcion,
  accion,
  ajustado = false,
  children,
  sx,
  id,
}: {
  titulo?: string;
  descripcion?: ReactNode;
  accion?: ReactNode;
  /** Sin relleno interior: para tablas, que traen el suyo. */
  ajustado?: boolean;
  children: ReactNode;
  sx?: object;
  id?: string;
}) {
  const cabecera = Boolean(titulo || accion);
  return (
    <Paper
      variant="outlined"
      component="section"
      id={id}
      sx={{ overflow: 'hidden', scrollMarginTop: 'var(--space-8)', ...sx }}
    >
      {cabecera && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            gap: 'var(--space-4)',
            px: 'var(--density-card-p)',
            pt: 'var(--space-4)',
            pb: ajustado ? 'var(--space-4)' : 'var(--space-3)',
            minHeight: 48,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" component="h2">
              {titulo}
            </Typography>
            {descripcion && (
              <Typography
                variant="body2"
                component="div"
                sx={{ mt: 'var(--space-1)', mb: 'var(--space-2)', color: 'var(--text-secondary)' }}
              >
                {descripcion}
              </Typography>
            )}
          </Box>
          {accion}
        </Stack>
      )}
      <Box
        sx={
          ajustado
            ? cabecera
              ? { borderTop: '1px solid var(--border-hairline)' }
              : undefined
            : cabecera
              ? { px: 'var(--density-card-p)', pb: 'var(--density-card-p)' }
              : { p: 'var(--density-card-p)' }
        }
      >
        {children}
      </Box>
    </Paper>
  );
}

/**
 * Identificador clinico: expediente, folio, cedula, huella de firma.
 *
 * Va en monoespaciada con cifras tabulares porque se compara digito a digito
 * entre la pantalla y el papel.
 */
export function Dato({
  children,
  tono = 'normal',
}: {
  children: ReactNode;
  tono?: 'normal' | 'tenue';
}) {
  return (
    <Box
      component="span"
      className="dato"
      sx={{ color: tono === 'tenue' ? 'var(--text-secondary)' : 'inherit' }}
    >
      {children}
    </Box>
  );
}

/**
 * Folio en su canal.
 *
 * Todo objeto clinico del producto lleva un identificador CONSECUTIVO por
 * consultorio: `00042` la paciente, `C-00001` el estudio, `R-00001` la receta,
 * `N-00002` la nota. Se les da un canal de ancho fijo a la izquierda de cada
 * lista. No es decoracion: la numeracion es real y es la forma en que el
 * consultorio se refiere a sus documentos por telefono.
 */
export function Folio({
  children,
  tono = 'tenue',
}: {
  children: ReactNode;
  tono?: 'normal' | 'tenue';
}) {
  return (
    <Box
      component="span"
      className="dato"
      sx={{
        display: 'inline-block',
        minWidth: CANAL_FOLIO,
        fontSize: 'var(--text-mono)',
        color: tono === 'tenue' ? 'var(--text-secondary)' : 'var(--text-primary)',
      }}
    >
      {children}
    </Box>
  );
}

/** Par etiqueta/valor, en columna. La unidad de lectura de una ficha densa. */
export function Campo({
  etiqueta,
  children,
  ancho,
}: {
  etiqueta: string;
  children: ReactNode;
  ancho?: number | string;
}) {
  return (
    <Box sx={{ minWidth: ancho, breakInside: 'avoid' }}>
      <Typography
        variant="caption"
        component="div"
        sx={{ lineHeight: 1.4, color: 'var(--text-secondary)', fontWeight: 500 }}
      >
        {etiqueta}
      </Typography>
      <Typography
        variant="body1"
        component="div"
        sx={{ mt: 'var(--space-1)', color: 'var(--text-primary)', overflowWrap: 'anywhere' }}
      >
        {children}
      </Typography>
    </Box>
  );
}

/**
 * Titulo de pantalla: donde estoy, que importa, que hago.
 *
 * - `contexto`: linea superior (vuelta a la lista, modulo). Responde «donde».
 * - `detalle`: la cuenta o el estado que importa de un vistazo.
 * - `acciones`: UNA principal (contained) y, si hace falta, secundarias
 *   (outlined/text). Mas de tres acciones van a un menu.
 */
export function Encabezado({
  titulo,
  contexto,
  detalle,
  acciones,
}: {
  titulo: ReactNode;
  contexto?: ReactNode;
  detalle?: ReactNode;
  acciones?: ReactNode;
}) {
  return (
    <Stack
      component="header"
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ sm: 'flex-end' }}
      useFlexGap
      flexWrap="wrap"
      sx={{ mb: 'var(--density-section-gap)', gap: 'var(--space-5)' }}
    >
      {/* Base flexible solo en fila: en columna (movil) seria una altura. */}
      <Box sx={{ minWidth: 0, flex: { sm: '1 1 320px' } }}>
        {contexto && (
          <Box
            sx={{
              mb: 'var(--space-2)',
              fontSize: 'var(--text-body-dense)',
              color: 'var(--text-secondary)',
            }}
          >
            {contexto}
          </Box>
        )}
        <Typography variant="h1">{titulo}</Typography>
        {detalle && (
          <Typography
            variant="body1"
            component="div"
            sx={{ mt: 'var(--space-2)', color: 'var(--text-secondary)', maxWidth: '72ch' }}
          >
            {detalle}
          </Typography>
        )}
      </Box>
      {acciones && (
        <Stack
          direction="row"
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={{ flexShrink: 0, gap: 'var(--space-3)' }}
        >
          {acciones}
        </Stack>
      )}
    </Stack>
  );
}

/** Enlace de vuelta en la linea de contexto del encabezado. */
export function Volver({ a, children }: { a: string; children: ReactNode }) {
  return (
    <Box
      component={EnlaceRuta}
      to={a}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        minHeight: 'var(--target-min)',
        ml: '-4px',
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontWeight: 500,
        borderRadius: 'var(--radius-xs)',
        '&:hover': { color: 'var(--text-primary)', textDecoration: 'underline' },
      }}
    >
      <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
      {children}
    </Box>
  );
}

export function Cargando({ etiqueta = 'Cargando' }: { etiqueta?: string }) {
  return (
    <Stack
      role="status"
      aria-live="polite"
      alignItems="center"
      sx={{ py: 'var(--space-10)', gap: 'var(--space-4)' }}
    >
      <CircularProgress size={20} thickness={4} sx={{ color: 'var(--accent-emphasis)' }} />
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
        {etiqueta}
      </Typography>
    </Stack>
  );
}

/**
 * Esqueleto de tabla mientras llega la primera pagina.
 *
 * Se prefiere al giro centrado cuando ya se sabe la forma de lo que viene: la
 * pantalla no salta cuando llegan los datos.
 */
export function EsqueletoDeTabla({
  filas = 6,
  columnas = 4,
}: {
  filas?: number;
  columnas?: number;
}) {
  return (
    <Box role="status" aria-label="Cargando" sx={{ py: 'var(--space-2)' }}>
      {Array.from({ length: filas }, (_, fila) => (
        <Stack
          key={fila}
          direction="row"
          alignItems="center"
          sx={{
            minHeight: 'var(--density-row-h)',
            gap: 'var(--space-5)',
            px: 'var(--density-row-x)',
            borderBottom: '1px solid var(--border-hairline)',
            '&:last-child': { borderBottom: 0 },
          }}
        >
          {Array.from({ length: columnas }, (_, columna) => (
            <Skeleton
              key={columna}
              variant="text"
              width={columna === 0 ? 70 : `${100 / columnas}%`}
              height={18}
            />
          ))}
        </Stack>
      ))}
    </Box>
  );
}

/** Bloque de estado comun: vacio, sin resultados, sin permiso, no encontrado. */
function BloqueDeEstado({
  icono,
  titulo,
  ayuda,
  accion,
  tono = 'neutro',
  compacto = false,
}: {
  icono?: ReactNode;
  titulo: string;
  ayuda?: ReactNode;
  accion?: ReactNode;
  tono?: 'neutro' | 'aviso';
  compacto?: boolean;
}) {
  return (
    <Stack
      alignItems="flex-start"
      sx={{
        gap: 'var(--space-3)',
        py: compacto ? 'var(--space-6)' : 'var(--space-9)',
        px: compacto ? 'var(--density-card-p)' : 'var(--space-7)',
        border: '1px dashed var(--border-strong)',
        borderRadius: 'var(--radius-card)',
        // Fondo suave en el color secundario del consultorio.
        bgcolor: tono === 'aviso' ? 'var(--bg-sunken)' : 'var(--highlight-bg)',
        '& > .MuiButtonBase-root, & > a': { mt: 'var(--space-2)' },
      }}
    >
      {icono && (
        <Box
          aria-hidden
          sx={{ color: 'var(--text-tertiary)', display: 'flex', '& svg': { fontSize: 24 } }}
        >
          {icono}
        </Box>
      )}
      <Typography variant="h3" component="p">
        {titulo}
      </Typography>
      {ayuda && (
        <Typography
          variant="body1"
          component="div"
          sx={{ color: 'var(--text-secondary)', maxWidth: '56ch' }}
        >
          {ayuda}
        </Typography>
      )}
      {accion}
    </Stack>
  );
}

/**
 * Sin registros TODAVIA.
 *
 * Una pantalla sin datos es una invitacion a actuar, no un aviso de que algo
 * falta: dice que se puede hacer y da el boton para hacerlo. No es lo mismo que
 * `SinResultados` (hay datos, el filtro no deja ver ninguno) ni que un error
 * (no se pudo saber si hay datos).
 */
export function SinDatos({
  titulo,
  ayuda,
  accion,
  compacto,
}: {
  titulo: string;
  ayuda?: ReactNode;
  accion?: ReactNode;
  compacto?: boolean;
}) {
  return <BloqueDeEstado titulo={titulo} ayuda={ayuda} accion={accion} compacto={compacto} />;
}

/** Hay registros, pero ninguno pasa el filtro o la busqueda. */
export function SinResultados({
  titulo = 'Sin resultados',
  ayuda,
  alLimpiar,
  compacto,
}: {
  titulo?: string;
  ayuda?: ReactNode;
  alLimpiar?: () => void;
  compacto?: boolean;
}) {
  return (
    <BloqueDeEstado
      icono={<SearchOffRoundedIcon />}
      titulo={titulo}
      ayuda={ayuda}
      compacto={compacto}
      accion={
        alLimpiar && (
          <Button variant="outlined" onClick={alLimpiar}>
            Limpiar filtros
          </Button>
        )
      }
    />
  );
}

/** El rol de quien esta dentro no incluye esto. El servidor tambien lo niega. */
export function SinPermiso({
  titulo = 'Esta sección no está disponible para su rol',
  ayuda = 'Si necesita acceso, pídalo a quien administra el consultorio.',
  accion,
}: {
  titulo?: string;
  ayuda?: ReactNode;
  accion?: ReactNode;
}) {
  return (
    <BloqueDeEstado
      icono={<LockRoundedIcon />}
      titulo={titulo}
      ayuda={ayuda}
      accion={accion}
      tono="aviso"
    />
  );
}

/** El registro no existe (o es de otro consultorio: el servidor responde igual). */
export function NoEncontrado({
  titulo = 'No encontramos lo que busca',
  ayuda = 'Puede que el enlace esté incompleto o que el registro ya no exista.',
  volverA = '/',
  textoVolver = 'Ir al inicio',
}: {
  titulo?: string;
  ayuda?: ReactNode;
  volverA?: string;
  textoVolver?: string;
}) {
  return (
    <BloqueDeEstado
      icono={<SearchOffRoundedIcon />}
      titulo={titulo}
      ayuda={ayuda}
      tono="aviso"
      accion={
        <Button component={EnlaceRuta} to={volverA} variant="outlined">
          {textoVolver}
        </Button>
      }
    />
  );
}

/**
 * Error de la API.
 *
 * Dice que paso y, cuando se sabe, como resolverlo. Con `alReintentar` ofrece
 * el reintento: es la diferencia entre «no se pudo cargar» y «no hay nada».
 */
export function ErrorDeApi({
  error,
  alReintentar,
  titulo,
}: {
  error: unknown;
  alReintentar?: () => void;
  /** Que se intentaba: «No se pudo cargar la agenda». */
  titulo?: string;
}) {
  if (!error) return null;
  const detalle = errorDeApi(error);
  const sinConexion = tipoDeFallo(error) === 'sin-conexion';

  const campos =
    Array.isArray(detalle.detalles) &&
    (detalle.detalles as Array<{ campo?: string; mensaje?: string }>).filter((d) => d?.mensaje);

  return (
    <Alert
      severity="error"
      icon={sinConexion ? <CloudOffRoundedIcon fontSize="inherit" /> : undefined}
      sx={{ mb: 'var(--space-5)' }}
      action={
        alReintentar && (
          <Button
            color="inherit"
            size="small"
            startIcon={<ReplayRoundedIcon />}
            onClick={alReintentar}
          >
            Reintentar
          </Button>
        )
      }
    >
      {titulo && (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>
      )}
      <Typography variant="body2" sx={{ fontWeight: titulo ? 400 : 600 }}>
        {sinConexion
          ? 'No hay conexión con el servidor. Revise su red e intente de nuevo.'
          : detalle.mensaje}
      </Typography>
      {campos && campos.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 'var(--space-2)', pl: 'var(--space-6)' }}>
          {campos.map((campo, indice) => (
            <Typography component="li" variant="caption" key={`${campo.campo}-${indice}`}>
              {campo.campo ? `${campo.campo}: ` : ''}
              {campo.mensaje}
            </Typography>
          ))}
        </Box>
      )}
    </Alert>
  );
}

/**
 * Fallo al CARGAR una pantalla o un bloque: elige la respuesta correcta.
 *
 * 404 → no encontrado; 403 → sin permiso; lo demas → error con reintento.
 */
export function FalloDeCarga({
  error,
  alReintentar,
  titulo,
  noEncontrado,
}: {
  error: unknown;
  alReintentar?: () => void;
  titulo?: string;
  noEncontrado?: { titulo?: string; ayuda?: ReactNode; volverA?: string; textoVolver?: string };
}) {
  const tipo = tipoDeFallo(error);
  if (tipo === 'no-encontrado') return <NoEncontrado {...noEncontrado} />;
  if (tipo === 'sin-permiso') return <SinPermiso />;
  return <ErrorDeApi error={error} alReintentar={alReintentar} titulo={titulo} />;
}

/**
 * Boton de una accion que va al servidor.
 *
 * Mientras la operacion esta en curso se deshabilita (no hay doble envio), lo
 * dice con texto y con `aria-busy`, y conserva su ancho.
 */
export const BotonDeAccion = forwardRef<
  HTMLButtonElement,
  ButtonProps & { pendiente?: boolean; textoPendiente?: string }
>(function BotonDeAccion(
  { pendiente = false, textoPendiente, children, disabled, startIcon, ...resto },
  ref,
) {
  return (
    <Button
      ref={ref}
      {...resto}
      disabled={disabled || pendiente}
      aria-busy={pendiente || undefined}
      startIcon={pendiente ? <CircularProgress size={14} thickness={5} /> : startIcon}
    >
      {pendiente && textoPendiente ? textoPendiente : children}
    </Button>
  );
});

/**
 * Barra de acciones de un formulario, pegada al pie del area de trabajo.
 *
 * En movil queda siempre a la mano del pulgar. `aviso` explica por que la
 * accion principal esta deshabilitada, en lugar de dejar adivinar.
 */
export function PieDeFormulario({
  children,
  aviso,
  secundarias,
}: {
  /** Accion principal (y su atajo, si lo tiene). */
  children: ReactNode;
  aviso?: ReactNode;
  /** Acciones a la derecha: cancelar, dar de baja. */
  secundarias?: ReactNode;
}) {
  return (
    <Stack
      className="no-imprimir"
      direction="row"
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 'var(--z-pegajoso)',
        gap: 'var(--space-3)',
        mt: 'var(--space-5)',
        mx: { xs: '-16px', md: 0 },
        px: { xs: '16px', md: 0 },
        py: 'var(--space-4)',
        bgcolor: 'var(--bg-canvas)',
        borderTop: '1px solid var(--border-hairline)',
      }}
    >
      {children}
      {aviso && (
        <Typography
          variant="caption"
          sx={{ color: 'var(--text-secondary)', flexBasis: { xs: '100%', sm: 'auto' } }}
        >
          {aviso}
        </Typography>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {secundarias}
    </Stack>
  );
}

/** Contenedor de busqueda y filtros sobre una lista. */
export function BarraDeFiltros({ children }: { children: ReactNode }) {
  return (
    <Stack
      direction="row"
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
      role="search"
      sx={{ gap: 'var(--space-3)', mb: 'var(--space-5)' }}
    >
      {children}
    </Stack>
  );
}

/**
 * Paginacion de una lista paginada por el servidor.
 *
 * No se pinta si hay una sola pagina. Anuncia la pagina vigente.
 */
export function Paginacion({
  pagina,
  totalPaginas,
  alCambiar,
  deshabilitada = false,
}: {
  pagina: number;
  totalPaginas: number;
  alCambiar: (pagina: number) => void;
  deshabilitada?: boolean;
}) {
  if (totalPaginas <= 1) return null;
  return (
    <Stack
      component="nav"
      aria-label="Paginación"
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      sx={{ gap: 'var(--space-3)', mt: 'var(--space-4)' }}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<ChevronLeftRoundedIcon />}
        disabled={deshabilitada || pagina <= 1}
        onClick={() => alCambiar(pagina - 1)}
      >
        Anterior
      </Button>
      <Typography variant="body2" aria-live="polite" sx={{ color: 'var(--text-secondary)' }}>
        Página <Dato tono="tenue">{pagina}</Dato> de <Dato tono="tenue">{totalPaginas}</Dato>
      </Typography>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ChevronRightRoundedIcon />}
        disabled={deshabilitada || pagina >= totalPaginas}
        onClick={() => alCambiar(pagina + 1)}
      >
        Siguiente
      </Button>
    </Stack>
  );
}

/**
 * Tecla dibujada como tecla.
 *
 * Aparece junto a las acciones que tienen atajo. Se oculta con puntero tactil:
 * ahi no hay teclado que recordar.
 */
export function Tecla({ children }: { children: ReactNode }) {
  return (
    <Box
      component="kbd"
      sx={{
        display: 'inline-block',
        fontFamily: 'var(--font-mono)',
        // Piso de 13px: el atajo se lee para trabajar, no es adorno.
        fontSize: 'var(--text-label)',
        fontWeight: 500,
        lineHeight: 1,
        px: 'var(--space-2)',
        py: '3px',
        border: '1px solid var(--border-subtle)',
        borderBottomWidth: 2,
        borderRadius: 'var(--radius-xs)',
        color: 'var(--text-secondary)',
        bgcolor: 'var(--bg-sunken)',
        whiteSpace: 'nowrap',
        '@media (pointer: coarse)': { display: 'none' },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Punto de estado de 6px en `currentColor`, para leer el estado por forma
 * ademas de por tono. Va como `icon` de un `<Chip color="warning|error|info">`
 * o junto a una palabra. El exito NO lleva punto: lleva ✓ (el tema lo pone en
 * `<Chip color="success">`).
 */
export function PuntoDeEstado() {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: 'inline-block',
        flexShrink: 0,
        width: 6,
        height: 6,
        borderRadius: 'var(--radius-full)',
        bgcolor: 'currentColor',
      }}
    />
  );
}

/**
 * Marca de estado de las insignias (sistema de Pulso): neutro •, aviso !,
 * peligro ✕, en curso ◐. Va como `icon` del `<Chip>`, asi cada estado lleva
 * UNA sola marca. El exito no la usa: su ✓ lo pone el tema.
 */
export function MarcaDeEstado({ color }: { color: 'default' | 'warning' | 'error' | 'info' }) {
  if (color === 'default') return <PuntoDeEstado />;
  const simbolo = { warning: '!', error: '✕', info: '◐' }[color];
  return (
    <Box
      component="span"
      aria-hidden
      sx={{ fontWeight: 700, lineHeight: 1, fontSize: '0.8125rem', flexShrink: 0 }}
    >
      {simbolo}
    </Box>
  );
}

/**
 * Metrica: una cifra que el sistema YA calcula, con su etiqueta y una nota.
 *
 * Nunca se inventan metricas, tendencias ni graficas para llenarla. Con `a`
 * se vuelve enlace a donde vive la cifra. `destacada` marca la cifra que
 * importa con un filete de acento, sin bloques de color.
 */
export function Metrica({
  etiqueta,
  valor,
  nota,
  destacada = false,
  a,
}: {
  etiqueta: ReactNode;
  valor: ReactNode;
  nota?: ReactNode;
  destacada?: boolean;
  a?: string;
}) {
  const largo = typeof valor === 'string' && valor.length > 9;
  const enlace = a ? { component: EnlaceRuta, to: a } : {};
  return (
    <Box
      {...enlace}
      sx={{
        position: 'relative',
        display: 'grid',
        rowGap: 'var(--space-3)',
        alignContent: 'start',
        minHeight: { md: 152 },
        p: 'var(--density-card-p)',
        borderRadius: 'var(--radius-card)',
        containerType: 'inline-size',
        border: '1px solid var(--border-card)',
        boxShadow: 'var(--shadow-1)',
        textDecoration: 'none',
        transition: 'var(--transition-colors)',
        // La destacada es el UNICO bloque profundo de la pantalla (Pulso): fondo
        // en el color del consultorio y, en el telefono, a todo el ancho.
        ...(destacada
          ? {
              gridColumn: { xs: '1 / -1', md: 'auto' },
              background: 'linear-gradient(135deg, var(--accent-strong), var(--accent-strong-2))',
              borderColor: 'transparent',
              color: 'var(--accent-on)',
            }
          : { bgcolor: 'var(--bg-raised)', color: 'var(--text-primary)' }),
        ...(a && !destacada
          ? { '&:hover': { borderColor: 'var(--border-strong)', bgcolor: 'var(--bg-surface)' } }
          : {}),
        ...(a && destacada ? { '&:hover': { background: 'var(--accent-strong-2)' } } : {}),
      }}
    >
      {/* Como la StatCard de Telmo: si lleva a otra pantalla, un circulo con
          la flecha en la esquina; si no, nada que parezca boton. */}
      {a && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 'var(--density-card-p)',
            right: 'var(--density-card-p)',
            display: 'grid',
            placeItems: 'center',
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-full)',
            ...(destacada
              ? { bgcolor: 'var(--accent-on)', color: 'var(--accent-deep)' }
              : {
                  border: '1px solid var(--border-subtle)',
                  bgcolor: 'var(--bg-raised)',
                  color: 'var(--text-primary)',
                }),
            '& svg': { fontSize: 16 },
          }}
        >
          <NorthEastRoundedIcon />
        </Box>
      )}
      <Box
        sx={{
          pr: a ? 'calc(32px + var(--space-3))' : 0,
          fontSize: 'var(--text-body-dense)',
          fontWeight: 600,
          lineHeight: 'var(--leading-snug)',
          color: destacada ? 'var(--accent-on)' : 'var(--text-primary)',
        }}
      >
        {etiqueta}
      </Box>
      <Box
        className="cifra"
        sx={{
          mt: 'var(--space-3)',
          fontFamily: 'var(--font-head)',
          fontSize: largo ? 'clamp(1rem, 10.5cqi, 1.5rem)' : 'var(--text-numeral)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: largo ? 1.2 : 1.1,
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        {valor}
      </Box>
      {nota && (
        <Box
          sx={{
            fontSize: 'var(--text-body-dense)',
            color: destacada ? 'var(--accent-on-muted)' : 'var(--text-secondary)',
          }}
        >
          {nota}
        </Box>
      )}
    </Box>
  );
}

/** Fecha AAAA-MM-DD a texto legible, sin construir un instante. */
export function fechaLegible(fecha: string | undefined | null): string {
  if (!fecha) return '';
  const partes = fecha.slice(0, 10).split('-');
  if (partes.length !== 3) return fecha;
  const meses = [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ];
  return `${Number(partes[2])} ${meses[Number(partes[1]) - 1] ?? ''} ${partes[0]}`;
}
